import React, { useState } from 'react';
import { 
  CheckCircle, Clock, Calendar, ChevronDown, ChevronUp, 
  Rocket, AlertTriangle, Sparkles, Code, Coffee, Video, Wifi
} from 'lucide-react';

const Roadmap = () => {
  const [selectedFilter, setSelectedFilter] = useState('All Items');
  const [expandedQuarters, setExpandedQuarters] = useState(['q1-2026']);

  const quarters = [
    {
      id: 'q1-2025',
      quarter: 'Q1 2025',
      period: 'January - March 2025',
      status: 'Completed',
      highlight: 'V3.0 Launch',
      items: [
        { title: 'V3.0 Release', description: 'Major version update with WebRTC, 34 automations, microservices architecture', icon: Rocket },
        { title: 'WebRTC Low-Latency', description: '300-500ms voice response time via direct peer connection', icon: Sparkles },
        { title: 'Spotify Integration', description: '8 new music control commands with full playback support', icon: Sparkles },
        { title: 'Enhanced Security', description: 'DTLS encryption, RLS policies, JWT authentication', icon: CheckCircle },
      ]
    },
    {
      id: 'q2-2025',
      quarter: 'Q2 2025',
      period: 'April - June 2025',
      status: 'Completed',
      highlight: 'Workflow Bundles',
      items: [
        { 
          title: '"Start Dev" Command', 
          description: 'One voice command to launch your entire dev environment: VS Code, terminal, browser, Spotify playlist', 
          icon: Code,
          saves: '5 mins/session'
        },
        { 
          title: '"Deep Work" Command', 
          description: 'Block distractions, enable DND, start focus timer, play concentration music', 
          icon: Coffee,
          saves: '15 mins/day'
        },
        { 
          title: '"Content Creator" Command', 
          description: 'Launch OBS, arrange streaming windows, open chat panels, start recording', 
          icon: Video,
          saves: '10 mins/session'
        },
        { title: 'Firefox Support', description: 'Mozilla Firefox browser automation support', icon: Sparkles },
        { title: 'Local LLM Support (Ollama)', description: 'Run AI models locally for offline voice processing', icon: Sparkles },
      ]
    },
    {
      id: 'q3-2025',
      quarter: 'Q3 2025',
      period: 'July - September 2025',
      status: 'Completed',
      highlight: 'Platform Expansion',
      items: [
        { title: 'Mac Build', description: 'Native macOS application with Apple Silicon support', icon: Sparkles },
        { title: 'Linux Build', description: 'Support for Ubuntu, Fedora, and Arch Linux', icon: Sparkles },
        { title: 'Plugin System', description: 'Community plugin marketplace for custom automations', icon: Sparkles },
      ]
    },
    {
      id: 'q1-2026',
      quarter: 'Q1 2026',
      period: 'January - March 2026',
      status: 'In Progress',
      highlight: 'Power User Features',
      items: [
        { title: 'Mobile Companion App', description: 'Control your desktop from your phone (iOS & Android)', icon: Sparkles },
        { title: 'Advanced Voice Commands', description: 'Multi-step commands with context awareness', icon: Sparkles },
        { title: 'Team Collaboration', description: 'Share automations with your team members', icon: Sparkles },
        { title: 'VS Code Controlling', description: 'Full VS Code integration: open files, run commands, navigate code', icon: Code },
        { title: 'Notion Controlling', description: 'Automate Notion workspace: create pages, update databases, search notes', icon: Sparkles },
        { title: 'Deep Research & Page Interaction', description: 'Research information online and interact with web pages intelligently', icon: Sparkles },
        { title: 'Clock System & Reminders', description: 'Pomodoro timer, reminders, and time tracking via voice', icon: Sparkles },
        { title: 'Desktop Background Switcher', description: 'Change desktop wallpaper with voice commands', icon: Sparkles },
        { title: 'Local File Processing', description: 'Read files and create content (e.g., "Read MrBeast tutorial then create Marketing Strategy")', icon: Sparkles },
        { title: 'Automated App Downloads', description: 'Download and install apps from the internet via voice', icon: Sparkles }
      ]
    },
    {
      id: 'q2-2026',
      quarter: 'Q2 2026',
      period: 'April - June 2026',
      status: 'Planning',
      items: [
        { title: 'Cloud Sync', description: 'Sync custom scripts across all your devices', icon: Sparkles },
        { title: 'Marketplace Launch', description: 'Buy and sell automation scripts from community creators', icon: Sparkles },
        { title: 'Enterprise Features', description: 'Admin dashboard, usage analytics, SSO integration', icon: Sparkles }
      ]
    }
  ];

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Completed': return <CheckCircle className="w-5 h-5" />;
      case 'In Progress': return <Clock className="w-5 h-5" />;
      case 'Upcoming': return <Calendar className="w-5 h-5" />;
      case 'Planning': return <Calendar className="w-5 h-5" />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Completed': return 'bg-green-500/20 text-green-500 border-green-500/30';
      case 'In Progress': return 'bg-orange-500/20 text-orange-500 border-orange-500/30';
      case 'Upcoming': return 'bg-blue-500/20 text-blue-500 border-blue-500/30';
      case 'Planning': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-500 border-gray-500/30';
    }
  };

  const allStatuses = ['All Items', 'Completed', 'In Progress', 'Planning'];
  const statusCounts = {
    'All Items': quarters.reduce((acc, q) => acc + q.items.length, 0),
    'Completed': quarters.filter(q => q.status === 'Completed').reduce((acc, q) => acc + q.items.length, 0),
    'In Progress': quarters.filter(q => q.status === 'In Progress').reduce((acc, q) => acc + q.items.length, 0),
    'Planning': quarters.filter(q => q.status === 'Planning').reduce((acc, q) => acc + q.items.length, 0)
  };

  const filteredQuarters = quarters.filter(quarter => {
    if (selectedFilter === 'All Items') return true;
    return quarter.status === selectedFilter;
  });

  const toggleQuarter = (quarterId) => {
    setExpandedQuarters(prev =>
      prev.includes(quarterId)
        ? prev.filter(id => id !== quarterId)
        : [...prev, quarterId]
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12" data-testid="roadmap-header">
          <div className="inline-flex items-center space-x-2 mb-4">
            <Rocket className="w-6 h-6 text-orange-500" />
            <span className="text-orange-500 font-semibold uppercase tracking-wider text-sm">
              V3.0 and Beyond
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Product Roadmap
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            See what we're building and what's coming next. Your feedback shapes our priorities.
          </p>
        </div>

        {/* Addressing User Pains Banner */}
        <div className="mb-12 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Wifi className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-2 flex items-center">
                <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
                Addressing User Pains: Future Offline Mode
              </h3>
              <p className="text-gray-300">
                We hear you! Many users want Twe to work without internet. We're completing <strong className="text-green-400">Local LLM Support via Ollama</strong> (Q3 2025) 
                to enable fully offline voice processing. This lets you run AI models on your own hardware with zero cloud dependency.
              </p>
              <p className="text-gray-400 mt-2 text-sm">
                Note: Offline mode trades some accuracy for privacy and availability. Cloud mode still offers the best accuracy.
              </p>
            </div>
          </div>
        </div>

        {/* Filter by Status */}
        <div className="mb-12">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {allStatuses.map((status) => (
              <button
                key={status}
                onClick={() => setSelectedFilter(status)}
                data-testid={`filter-${status.toLowerCase().replace(' ', '-')}`}
                className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                  selectedFilter === status
                    ? 'bg-orange-500 text-white scale-105'
                    : 'bg-zinc-900 text-gray-400 hover:bg-zinc-800 border border-white/10'
                }`}
              >
                {status}
                <span className="ml-2 text-sm opacity-75">
                  ({statusCounts[status]})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Quarters Timeline */}
        <div className="space-y-6">
          {filteredQuarters.map((quarter) => {
            const isExpanded = expandedQuarters.includes(quarter.id);
            
            return (
              <div
                key={quarter.id}
                data-testid={`quarter-${quarter.id}`}
                className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-orange-500/30 transition-all duration-300"
              >
                {/* Quarter Header */}
                <button
                  onClick={() => toggleQuarter(quarter.id)}
                  className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors duration-200"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-6">
                    {/* Quarter Label */}
                    <div className="text-left">
                      <h2 className="text-2xl sm:text-3xl font-bold text-orange-500 mb-1">
                        {quarter.quarter}
                      </h2>
                      <p className="text-gray-400 text-sm">{quarter.period}</p>
                    </div>

                    {/* Status Badge */}
                    <div className={`flex items-center space-x-2 px-4 py-2 rounded-full border ${getStatusColor(quarter.status)}`}>
                      {getStatusIcon(quarter.status)}
                      <span className="font-medium">{quarter.status}</span>
                    </div>

                    {/* Highlight Badge */}
                    {quarter.highlight && (
                      <span className="px-3 py-1 bg-orange-500/20 text-orange-400 text-sm font-medium rounded-full border border-orange-500/30">
                        {quarter.highlight}
                      </span>
                    )}
                  </div>

                  {/* Expand Icon */}
                  <div className="flex-shrink-0 ml-4">
                    {isExpanded ? (
                      <ChevronUp className="w-6 h-6 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Quarter Items */}
                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-white/5">
                    <div className="mt-6 grid md:grid-cols-2 gap-4">
                      {quarter.items.map((item, idx) => {
                        const ItemIcon = item.icon || Sparkles;
                        return (
                          <div
                            key={idx}
                            className="bg-black/40 rounded-lg p-5 border border-white/5 hover:border-orange-500/30 transition-all duration-300"
                          >
                            <div className="flex items-start space-x-3">
                              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                <ItemIcon className="w-5 h-5 text-orange-500" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-white mb-1">
                                  {item.title}
                                </h3>
                                <p className="text-gray-400 text-sm">{item.description}</p>
                                
                                {/* Time Savings Badge */}
                                {item.saves && (
                                  <span className="inline-block mt-2 px-2 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded border border-green-500/30">
                                    Saves {item.saves}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Progress bar for In Progress items */}
                            {quarter.status === 'In Progress' && (
                              <div className="mt-4">
                                <div className="flex justify-between text-xs text-gray-500 mb-1">
                                  <span>Progress</span>
                                  <span>{Math.floor(Math.random() * 30) + 50}%</span>
                                </div>
                                <div className="w-full bg-zinc-800 rounded-full h-1.5">
                                  <div 
                                    className="bg-gradient-to-r from-orange-600 to-red-600 h-1.5 rounded-full transition-all duration-500"
                                    style={{ width: `${Math.floor(Math.random() * 30) + 50}%` }}
                                  ></div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* No Results */}
        {filteredQuarters.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">
              No items found for this status.
            </p>
          </div>
        )}

        {/* Honesty Note */}
        <div className="mt-12 bg-zinc-900/50 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-2 flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
            Honesty Note
          </h3>
          <p className="text-gray-400">
            Roadmap items are subject to change based on user feedback, technical feasibility, and business priorities. 
            We share our plans transparently but cannot guarantee exact timelines. 
            <strong className="text-orange-400"> Join our Discord</strong> to vote on features and influence our direction.
          </p>
        </div>

        {/* Request Feature Section */}
        <div className="mt-12 bg-gradient-to-br from-zinc-900 to-black border border-orange-500/30 rounded-xl p-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Have a feature request?
          </h2>
          <p className="text-gray-400 mb-6">
            We're always looking for ways to improve Twe. Your feedback directly shapes our roadmap!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="github-request-link"
              className="px-6 py-3 bg-zinc-800 text-white font-medium rounded-lg hover:bg-zinc-700 transition-colors duration-200"
            >
              Submit on GitHub
            </a>
            <a
              href="https://discord.gg/AUezcNccMd"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="discord-request-link"
              className="px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white font-medium rounded-lg hover:shadow-lg hover:shadow-orange-500/50 transition-all duration-300"
            >
              Discuss on Discord
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Roadmap;
